/**
 * Track C — SVG vector import.
 * Parses SVG text into a VectorDocument: full path-data flattener (M L H V C S
 * Q T A Z, relative + absolute, smooth-control reflection, elliptical arcs via
 * <= PI/4 cubic spans, quadratics elevated to cubics), the basic shapes
 * (rect incl. rx/ry, circle, ellipse, line, polyline, polygon), transform
 * composition (matrix/translate/scale/rotate/skewX/skewY) through nested groups,
 * and paint -> layer mapping (stroke color if real, else fill; exact
 * LIGHTBURN_PALETTE match keeps its C-index, unmatched colors take the lowest
 * unused indices in encounter order). Units: px/unitless treated as mm; when
 * width/height carry mm/cm/in a user-unit->mm scale is derived from the viewBox
 * (LightBurn SVG import convention) and applied as a root transform.
 *
 * A small dependency-free XML element-tree parser is embedded locally so this
 * module unit-tests in node without DOMParser and stays isolated from the
 * concurrently-built lbrn parser.
 */
import type {
  ImportWarning,
  LayerInfo,
  Mat,
  PolyGeom,
  Pt,
  VectorDocument,
} from "../core/types";
import { IDENTITY, LIGHTBURN_PALETTE } from "../core/types";
import { applyMat, flattenCubic, matMul } from "../core/geometry";

// ---------------------------------------------------------------------------
// Minimal XML tree (private). Entities/CDATA decoded on read; comments, PIs,
// DOCTYPE skipped; self-closing tags handled. Namespace prefixes stripped for
// element matching (localName), attribute lookup is case-insensitive-tolerant.
// ---------------------------------------------------------------------------

interface XmlText {
  kind: "text";
  text: string;
}

type XmlNode = XmlElement | XmlText;

class XmlElement {
  readonly kind = "element";
  tag: string;
  attrs: { name: string; value: string }[] = [];
  children: XmlNode[] = [];
  private lc: Map<string, string> | null = null;

  constructor(tag: string) {
    this.tag = tag;
  }

  get localName(): string {
    let t = this.tag;
    const brace = t.indexOf("}");
    if (brace >= 0) t = t.slice(brace + 1);
    const colon = t.indexOf(":");
    if (colon >= 0) t = t.slice(colon + 1);
    return t;
  }

  private lcMap(): Map<string, string> {
    if (!this.lc) {
      this.lc = new Map();
      for (const a of this.attrs) {
        const key = a.name.toLowerCase();
        if (!this.lc.has(key)) this.lc.set(key, a.value);
      }
    }
    return this.lc;
  }

  attr(...names: string[]): string | null {
    for (const nm of names) {
      for (const a of this.attrs) if (a.name === nm) return a.value;
    }
    const map = this.lcMap();
    for (const nm of names) {
      const v = map.get(nm.toLowerCase());
      if (v !== undefined) return v;
    }
    return null;
  }

  setAttr(name: string, value: string): void {
    this.attrs.push({ name, value });
    this.lc = null;
  }
}

function isSpace(ch: string): boolean {
  return ch === " " || ch === "\t" || ch === "\n" || ch === "\r" || ch === "\f" || ch === "\v";
}

function decodeEntities(s: string): string {
  if (s.indexOf("&") < 0) return s;
  return s.replace(/&(#x[0-9A-Fa-f]+|#\d+|[A-Za-z][A-Za-z0-9]*);/g, (full: string, ent: string) => {
    if (ent[0] === "#") {
      const code = ent[1] === "x" || ent[1] === "X" ? parseInt(ent.slice(2), 16) : parseInt(ent.slice(1), 10);
      if (Number.isFinite(code) && code >= 0 && code <= 0x10ffff) {
        try {
          return String.fromCodePoint(code);
        } catch {
          return full;
        }
      }
      return full;
    }
    switch (ent) {
      case "amp":
        return "&";
      case "lt":
        return "<";
      case "gt":
        return ">";
      case "quot":
        return '"';
      case "apos":
        return "'";
      default:
        return full;
    }
  });
}

function findTagEnd(s: string, start: number): number {
  let i = start + 1;
  let quote = "";
  const n = s.length;
  while (i < n) {
    const ch = s[i];
    if (quote) {
      if (ch === quote) quote = "";
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === ">") {
      return i;
    }
    i++;
  }
  return -1;
}

function parseStartTag(inner: string): XmlElement {
  const n = inner.length;
  let i = 0;
  while (i < n && isSpace(inner[i])) i++;
  const nameStart = i;
  while (i < n && !isSpace(inner[i]) && inner[i] !== "/") i++;
  const el = new XmlElement(inner.slice(nameStart, i));
  while (i < n) {
    while (i < n && (isSpace(inner[i]) || inner[i] === "/")) i++;
    if (i >= n) break;
    const attrNameStart = i;
    while (i < n && inner[i] !== "=" && !isSpace(inner[i]) && inner[i] !== "/") i++;
    const attrName = inner.slice(attrNameStart, i);
    if (!attrName) break;
    while (i < n && isSpace(inner[i])) i++;
    let value = "";
    if (inner[i] === "=") {
      i++;
      while (i < n && isSpace(inner[i])) i++;
      const q = inner[i];
      if (q === '"' || q === "'") {
        i++;
        const vs = i;
        while (i < n && inner[i] !== q) i++;
        value = inner.slice(vs, i);
        i++;
      } else {
        const vs = i;
        while (i < n && !isSpace(inner[i]) && inner[i] !== "/") i++;
        value = inner.slice(vs, i);
      }
    }
    el.setAttr(attrName, decodeEntities(value));
  }
  return el;
}

/** Parse XML text into its root element (namespace-agnostic, tolerant). */
function parseXml(input: string): XmlElement | null {
  const n = input.length;
  let i = 0;
  const roots: XmlNode[] = [];
  const stack: XmlElement[] = [];
  const push = (node: XmlNode): void => {
    if (stack.length) stack[stack.length - 1].children.push(node);
    else roots.push(node);
  };
  while (i < n) {
    const lt = input.indexOf("<", i);
    if (lt < 0) break;
    if (lt > i && stack.length) {
      const raw = input.slice(i, lt);
      if (raw.trim().length) push({ kind: "text", text: decodeEntities(raw) });
    }
    if (input.startsWith("<!--", lt)) {
      const end = input.indexOf("-->", lt + 4);
      i = end < 0 ? n : end + 3;
      continue;
    }
    if (input.startsWith("<![CDATA[", lt)) {
      const end = input.indexOf("]]>", lt + 9);
      const stop = end < 0 ? n : end;
      if (stack.length) push({ kind: "text", text: input.slice(lt + 9, stop) });
      i = end < 0 ? n : end + 3;
      continue;
    }
    if (input.startsWith("<?", lt)) {
      const end = input.indexOf("?>", lt + 2);
      i = end < 0 ? n : end + 2;
      continue;
    }
    if (input.startsWith("<!", lt)) {
      const end = input.indexOf(">", lt + 2);
      i = end < 0 ? n : end + 1;
      continue;
    }
    if (input.startsWith("</", lt)) {
      const end = input.indexOf(">", lt + 2);
      if (stack.length) stack.pop();
      i = end < 0 ? n : end + 1;
      continue;
    }
    const end = findTagEnd(input, lt);
    if (end < 0) break;
    let tagContent = input.slice(lt + 1, end);
    const selfClose = tagContent.endsWith("/");
    if (selfClose) tagContent = tagContent.slice(0, -1);
    const el = parseStartTag(tagContent);
    push(el);
    if (!selfClose) stack.push(el);
    i = end + 1;
  }
  for (const node of roots) if (node.kind === "element") return node;
  return null;
}

function childElements(el: XmlElement): XmlElement[] {
  const out: XmlElement[] = [];
  for (const c of el.children) if (c.kind === "element") out.push(c);
  return out;
}

/** Depth-first search for the first <svg> element (files sometimes wrap it). */
function findSvg(el: XmlElement): XmlElement | null {
  if (el.localName.toLowerCase() === "svg") return el;
  for (const c of childElements(el)) {
    const found = findSvg(c);
    if (found) return found;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Numbers / lengths.
// ---------------------------------------------------------------------------

function fnum(el: XmlElement, name: string, def = 0): number {
  const raw = el.attr(name);
  if (raw == null) return def;
  const v = parseFloat(raw.trim());
  return Number.isFinite(v) ? v : def;
}

/** Optional length (rect rx/ry): returns null for missing/"auto"/invalid. */
function optLen(el: XmlElement, name: string): number | null {
  const raw = el.attr(name);
  if (raw == null) return null;
  const t = raw.trim();
  if (t === "" || t.toLowerCase() === "auto") return null;
  const v = parseFloat(t);
  return Number.isFinite(v) ? v : null;
}

interface SvgLen {
  value: number;
  unit: string;
}

function parseSvgLength(raw: string | null): SvgLen | null {
  if (raw == null) return null;
  const m = /^\s*([+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)\s*([a-z%]*)\s*$/.exec(raw);
  if (!m) return null;
  const value = parseFloat(m[1]);
  if (!Number.isFinite(value)) return null;
  return { value, unit: m[2].toLowerCase() };
}

function unitToMm(unit: string): number | null {
  if (unit === "mm") return 1;
  if (unit === "cm") return 10;
  if (unit === "in") return 25.4;
  return null;
}

function parsePoints(raw: string | null): number[] {
  if (!raw) return [];
  const out: number[] = [];
  for (const tok of raw.trim().split(/[\s,]+/)) {
    if (!tok) continue;
    const v = parseFloat(tok);
    if (Number.isFinite(v)) out.push(v);
  }
  if (out.length % 2 === 1) out.pop();
  return out;
}

function transformFlat(userPts: number[], mat: Mat): Float64Array {
  const out = new Float64Array(userPts.length);
  for (let i = 0; i + 1 < userPts.length; i += 2) {
    const p = applyMat(mat, userPts[i], userPts[i + 1]);
    out[i] = p.x;
    out[i + 1] = p.y;
  }
  return out;
}

/** Drop a trailing point coincident with the first (Ring convention). */
function dropTrailingDup(out: number[]): void {
  if (out.length >= 8) {
    const dx = out[out.length - 2] - out[0];
    const dy = out[out.length - 1] - out[1];
    if (dx * dx + dy * dy < 1e-18) {
      out.pop();
      out.pop();
    }
  }
}

// ---------------------------------------------------------------------------
// Color parsing.
// ---------------------------------------------------------------------------

const NAMED_COLORS: Record<string, string> = {
  aqua: "#00FFFF",
  black: "#000000",
  blue: "#0000FF",
  fuchsia: "#FF00FF",
  gray: "#808080",
  grey: "#808080",
  green: "#008000",
  lime: "#00FF00",
  maroon: "#800000",
  navy: "#000080",
  olive: "#808000",
  purple: "#800080",
  red: "#FF0000",
  silver: "#C0C0C0",
  teal: "#008080",
  white: "#FFFFFF",
  yellow: "#FFFF00",
};

function hex2(n: number): string {
  const v = Math.max(0, Math.min(255, Math.round(n)));
  return v.toString(16).toUpperCase().padStart(2, "0");
}

function toHex(r: number, g: number, b: number): string {
  return "#" + hex2(r) + hex2(g) + hex2(b);
}

function parseChannel(tok: string): number | null {
  const t = tok.trim();
  if (t.endsWith("%")) {
    const p = parseFloat(t.slice(0, -1));
    return Number.isFinite(p) ? (p * 255) / 100 : null;
  }
  const v = parseFloat(t);
  return Number.isFinite(v) ? v : null;
}

/**
 * Parse a raw paint token to an uppercase "#RRGGBB", the literal "none", or
 * null (unknown/unsupported, e.g. url(...) gradients). Keywords currentColor
 * and inherit are resolved by the caller (they need the inheritance context).
 */
function parseColor(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  const low = v.toLowerCase();
  if (low === "none" || low === "transparent") return "none";
  if (v[0] === "#") {
    const h = v.slice(1);
    if (/^[0-9A-Fa-f]{3}$/.test(h)) {
      return ("#" + h[0] + h[0] + h[1] + h[1] + h[2] + h[2]).toUpperCase();
    }
    if (/^[0-9A-Fa-f]{4}$/.test(h)) {
      return ("#" + h[0] + h[0] + h[1] + h[1] + h[2] + h[2]).toUpperCase();
    }
    if (/^[0-9A-Fa-f]{6}$/.test(h)) return ("#" + h).toUpperCase();
    if (/^[0-9A-Fa-f]{8}$/.test(h)) return ("#" + h.slice(0, 6)).toUpperCase();
    return null;
  }
  if (low.startsWith("rgb")) {
    const open = v.indexOf("(");
    const close = v.indexOf(")");
    if (open >= 0 && close > open) {
      const parts = v
        .slice(open + 1, close)
        .split(/[\s,/]+/)
        .filter((s) => s.length > 0);
      if (parts.length >= 3) {
        const r = parseChannel(parts[0]);
        const g = parseChannel(parts[1]);
        const b = parseChannel(parts[2]);
        if (r != null && g != null && b != null) return toHex(r, g, b);
      }
    }
    return null;
  }
  const named = NAMED_COLORS[low];
  return named ?? null;
}

// ---------------------------------------------------------------------------
// Style / paint inheritance.
// ---------------------------------------------------------------------------

interface Ctx {
  color: string;
  fill: string; // "#RRGGBB" or "none"
  stroke: string; // "#RRGGBB" or "none"
  visibility: "visible" | "hidden";
}

const INITIAL_CTX: Ctx = { color: "#000000", fill: "#000000", stroke: "none", visibility: "visible" };

function parseStyle(el: XmlElement): Map<string, string> {
  const map = new Map<string, string>();
  const raw = el.attr("style");
  if (!raw) return map;
  for (const decl of raw.split(";")) {
    const idx = decl.indexOf(":");
    if (idx < 0) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const value = decl.slice(idx + 1).trim();
    if (prop) map.set(prop, value);
  }
  return map;
}

function styleProp(el: XmlElement, style: Map<string, string>, prop: string): string | null {
  const s = style.get(prop);
  if (s !== undefined) return s;
  return el.attr(prop);
}

function resolvePaint(raw: string, currentColor: string, parentValue: string): string {
  const v = raw.trim();
  if (!v) return parentValue;
  const low = v.toLowerCase();
  if (low === "inherit") return parentValue;
  if (low === "currentcolor") return currentColor;
  if (low.startsWith("url(")) return "none";
  const c = parseColor(v);
  return c === null ? parentValue : c;
}

function resolveColorProp(raw: string, parentColor: string): string {
  const low = raw.trim().toLowerCase();
  if (low === "inherit" || low === "currentcolor") return parentColor;
  const c = parseColor(raw);
  return c && c !== "none" ? c : parentColor;
}

function updateCtx(el: XmlElement, parent: Ctx, style: Map<string, string>): Ctx {
  let color = parent.color;
  const rawColor = styleProp(el, style, "color");
  if (rawColor != null) color = resolveColorProp(rawColor, parent.color);

  let fill = parent.fill;
  const rawFill = styleProp(el, style, "fill");
  if (rawFill != null) fill = resolvePaint(rawFill, color, parent.fill);

  let stroke = parent.stroke;
  const rawStroke = styleProp(el, style, "stroke");
  if (rawStroke != null) stroke = resolvePaint(rawStroke, color, parent.stroke);

  let visibility = parent.visibility;
  const rawVis = styleProp(el, style, "visibility");
  if (rawVis != null) {
    const v = rawVis.trim().toLowerCase();
    if (v === "hidden" || v === "collapse") visibility = "hidden";
    else if (v === "visible") visibility = "visible";
  }
  return { color, fill, stroke, visibility };
}

/** Effective paint of a drawable: stroke if real, else fill, else black. */
function paintOf(ctx: Ctx): string {
  if (ctx.stroke !== "none") return ctx.stroke;
  if (ctx.fill !== "none") return ctx.fill;
  return "#000000";
}

// ---------------------------------------------------------------------------
// Transform attribute parser.
// ---------------------------------------------------------------------------

const DEG = Math.PI / 180;

function parseTransform(raw: string | null): Mat {
  if (!raw) return IDENTITY;
  let m: Mat = IDENTITY;
  const re = /([a-zA-Z]+)\s*\(([^)]*)\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw)) !== null) {
    const name = match[1].toLowerCase();
    const nums = match[2]
      .trim()
      .split(/[\s,]+/)
      .filter((s) => s.length > 0)
      .map(Number);
    if (nums.some((v) => !Number.isFinite(v))) continue;
    let t: Mat | null = null;
    switch (name) {
      case "matrix":
        if (nums.length >= 6) t = [nums[0], nums[1], nums[2], nums[3], nums[4], nums[5]];
        break;
      case "translate":
        if (nums.length >= 1) t = [1, 0, 0, 1, nums[0], nums.length >= 2 ? nums[1] : 0];
        break;
      case "scale":
        if (nums.length >= 1) t = [nums[0], 0, 0, nums.length >= 2 ? nums[1] : nums[0], 0, 0];
        break;
      case "rotate": {
        if (nums.length >= 1) {
          const a = nums[0] * DEG;
          const cos = Math.cos(a);
          const sin = Math.sin(a);
          const rot: Mat = [cos, sin, -sin, cos, 0, 0];
          if (nums.length >= 3) {
            const cx = nums[1];
            const cy = nums[2];
            t = matMul(matMul([1, 0, 0, 1, cx, cy], rot), [1, 0, 0, 1, -cx, -cy]);
          } else {
            t = rot;
          }
        }
        break;
      }
      case "skewx":
        if (nums.length >= 1) t = [1, 0, Math.tan(nums[0] * DEG), 1, 0, 0];
        break;
      case "skewy":
        if (nums.length >= 1) t = [1, Math.tan(nums[0] * DEG), 0, 1, 0, 0];
        break;
      default:
        t = null;
    }
    if (t) m = matMul(m, t);
  }
  return m;
}

// ---------------------------------------------------------------------------
// Elliptical arc flattening (shared by path A, circle, ellipse, rounded rect).
// ---------------------------------------------------------------------------

function ellipsePoint(cx: number, cy: number, rx: number, ry: number, cosPhi: number, sinPhi: number, theta: number): Pt {
  const x = rx * Math.cos(theta);
  const y = ry * Math.sin(theta);
  return { x: cx + cosPhi * x - sinPhi * y, y: cy + sinPhi * x + cosPhi * y };
}

function ellipseDeriv(rx: number, ry: number, cosPhi: number, sinPhi: number, theta: number): Pt {
  const dx = -rx * Math.sin(theta);
  const dy = ry * Math.cos(theta);
  return { x: cosPhi * dx - sinPhi * dy, y: sinPhi * dx + cosPhi * dy };
}

/**
 * Append the flattened arc to `out` (mm space, excluding the start point,
 * including the end point). Center parameterization; split into <= PI/4 spans,
 * each approximated by a cubic then adaptively flattened.
 */
function flattenArcCenter(
  out: number[],
  mat: Mat,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  phi: number,
  theta0: number,
  delta: number,
  tol: number,
  maxSteps: number,
): void {
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);
  const segs = Math.max(1, Math.ceil(Math.abs(delta) / (Math.PI / 4)));
  const dSeg = delta / segs;
  const alpha = (4 / 3) * Math.tan(dSeg / 4);
  let theta = theta0;
  let e0 = ellipsePoint(cx, cy, rx, ry, cosPhi, sinPhi, theta);
  let d0 = ellipseDeriv(rx, ry, cosPhi, sinPhi, theta);
  for (let s = 0; s < segs; s++) {
    const theta1 = theta + dSeg;
    const e1 = ellipsePoint(cx, cy, rx, ry, cosPhi, sinPhi, theta1);
    const d1 = ellipseDeriv(rx, ry, cosPhi, sinPhi, theta1);
    const c1x = e0.x + alpha * d0.x;
    const c1y = e0.y + alpha * d0.y;
    const c2x = e1.x - alpha * d1.x;
    const c2y = e1.y - alpha * d1.y;
    const P0 = applyMat(mat, e0.x, e0.y);
    const C1 = applyMat(mat, c1x, c1y);
    const C2 = applyMat(mat, c2x, c2y);
    const P1 = applyMat(mat, e1.x, e1.y);
    flattenCubic(out, P0.x, P0.y, C1.x, C1.y, C2.x, C2.y, P1.x, P1.y, tol, maxSteps);
    theta = theta1;
    e0 = e1;
    d0 = d1;
  }
}

interface ArcCenter {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  phi: number;
  theta0: number;
  delta: number;
}

function angleBetween(ux: number, uy: number, vx: number, vy: number): number {
  const dot = ux * vx + uy * vy;
  const len = Math.sqrt((ux * ux + uy * uy) * (vx * vx + vy * vy));
  let a = len === 0 ? 0 : Math.acos(Math.max(-1, Math.min(1, dot / len)));
  if (ux * vy - uy * vx < 0) a = -a;
  return a;
}

/** Convert an SVG endpoint-parameterized arc to center parameterization. */
function arcToCenter(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  rxIn: number,
  ryIn: number,
  phiDeg: number,
  large: boolean,
  sweep: boolean,
): ArcCenter | null {
  let rx = Math.abs(rxIn);
  let ry = Math.abs(ryIn);
  if (rx < 1e-12 || ry < 1e-12) return null;
  const phi = phiDeg * DEG;
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);
  const dx = (x1 - x2) / 2;
  const dy = (y1 - y2) / 2;
  const x1p = cosPhi * dx + sinPhi * dy;
  const y1p = -sinPhi * dx + cosPhi * dy;
  let rx2 = rx * rx;
  let ry2 = ry * ry;
  const x1p2 = x1p * x1p;
  const y1p2 = y1p * y1p;
  const lambda = x1p2 / rx2 + y1p2 / ry2;
  if (lambda > 1) {
    const s = Math.sqrt(lambda);
    rx *= s;
    ry *= s;
    rx2 = rx * rx;
    ry2 = ry * ry;
  }
  const sign = large !== sweep ? 1 : -1;
  let num = rx2 * ry2 - rx2 * y1p2 - ry2 * x1p2;
  if (num < 0) num = 0;
  const den = rx2 * y1p2 + ry2 * x1p2;
  const co = den === 0 ? 0 : sign * Math.sqrt(num / den);
  const cxp = co * ((rx * y1p) / ry);
  const cyp = co * ((-ry * x1p) / rx);
  const cx = cosPhi * cxp - sinPhi * cyp + (x1 + x2) / 2;
  const cy = sinPhi * cxp + cosPhi * cyp + (y1 + y2) / 2;
  const ux = (x1p - cxp) / rx;
  const uy = (y1p - cyp) / ry;
  const vx = (-x1p - cxp) / rx;
  const vy = (-y1p - cyp) / ry;
  const theta0 = angleBetween(1, 0, ux, uy);
  let delta = angleBetween(ux, uy, vx, vy);
  if (!sweep && delta > 0) delta -= 2 * Math.PI;
  if (sweep && delta < 0) delta += 2 * Math.PI;
  return { cx, cy, rx, ry, phi, theta0, delta };
}

// ---------------------------------------------------------------------------
// Path data flattener.
// ---------------------------------------------------------------------------

const NUM_RE = /[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/y;
const CMD_LETTERS = "MmLlHhVvCcSsQqTtAaZz";

class PathLexer {
  private s: string;
  private i = 0;
  private n: number;

  constructor(s: string) {
    this.s = s;
    this.n = s.length;
  }

  private skipSep(): void {
    while (this.i < this.n) {
      const c = this.s[this.i];
      if (c === " " || c === "\t" || c === "\n" || c === "\r" || c === "\f" || c === ",") this.i++;
      else break;
    }
  }

  eof(): boolean {
    this.skipSep();
    return this.i >= this.n;
  }

  peekCommand(): string | null {
    this.skipSep();
    if (this.i >= this.n) return null;
    const c = this.s[this.i];
    return CMD_LETTERS.indexOf(c) >= 0 ? c : null;
  }

  readCommand(): string {
    const c = this.s[this.i];
    this.i++;
    return c;
  }

  readNumber(): number | null {
    this.skipSep();
    NUM_RE.lastIndex = this.i;
    const m = NUM_RE.exec(this.s);
    if (!m || m.index !== this.i) return null;
    this.i += m[0].length;
    return parseFloat(m[0]);
  }

  readFlag(): number | null {
    this.skipSep();
    if (this.i >= this.n) return null;
    const c = this.s[this.i];
    if (c === "0") {
      this.i++;
      return 0;
    }
    if (c === "1") {
      this.i++;
      return 1;
    }
    return null;
  }
}

interface SvgSubpath {
  points: Float64Array;
  closed: boolean;
}

/**
 * Flatten SVG path `d` data into transformed flat polylines (mm). Each subpath
 * is emitted separately; `closed` is true only for subpaths terminated by Z.
 */
function flattenPathCore(d: string, mat: Mat, tol: number, maxSteps: number): SvgSubpath[] {
  const subs: SvgSubpath[] = [];
  if (!d) return subs;
  const lex = new PathLexer(d);

  let curX = 0;
  let curY = 0;
  let startX = 0;
  let startY = 0;
  let cur: number[] = [];
  let curClosed = false;
  let hasCur = false;
  let prevCtrlX = 0;
  let prevCtrlY = 0;
  let prevCtrlKind = ""; // "C" (cubic) or "Q" (quadratic) or ""
  let cmd = "";
  let abort = false;

  const pushPt = (ux: number, uy: number): void => {
    const p = applyMat(mat, ux, uy);
    cur.push(p.x, p.y);
  };

  const finishSub = (): void => {
    if (hasCur) {
      if (curClosed) dropTrailingDup(cur);
      if (cur.length >= 4) subs.push({ points: Float64Array.from(cur), closed: curClosed });
    }
    hasCur = false;
    cur = [];
    curClosed = false;
  };

  const openSub = (ux: number, uy: number): void => {
    finishSub();
    startX = ux;
    startY = uy;
    curX = ux;
    curY = uy;
    cur = [];
    curClosed = false;
    hasCur = true;
    pushPt(ux, uy);
  };

  const ensureSub = (): void => {
    if (!hasCur) {
      startX = curX;
      startY = curY;
      cur = [];
      curClosed = false;
      hasCur = true;
      pushPt(curX, curY);
    }
  };

  const lineTo = (ux: number, uy: number): void => {
    ensureSub();
    pushPt(ux, uy);
    curX = ux;
    curY = uy;
    prevCtrlKind = "";
  };

  const cubicTo = (c1x: number, c1y: number, c2x: number, c2y: number, ex: number, ey: number): void => {
    ensureSub();
    const P0 = applyMat(mat, curX, curY);
    const C1 = applyMat(mat, c1x, c1y);
    const C2 = applyMat(mat, c2x, c2y);
    const P1 = applyMat(mat, ex, ey);
    flattenCubic(cur, P0.x, P0.y, C1.x, C1.y, C2.x, C2.y, P1.x, P1.y, tol, maxSteps);
    curX = ex;
    curY = ey;
  };

  const num = (): number => {
    const v = lex.readNumber();
    if (v === null) {
      abort = true;
      return 0;
    }
    return v;
  };

  const flag = (): number => {
    const v = lex.readFlag();
    if (v === null) {
      abort = true;
      return 0;
    }
    return v;
  };

  while (!lex.eof()) {
    const letter = lex.peekCommand();
    if (letter !== null) {
      cmd = lex.readCommand();
    } else {
      if (cmd === "") break;
      if (cmd === "Z" || cmd === "z") break; // no implicit repeat for close
      if (cmd === "M") cmd = "L";
      else if (cmd === "m") cmd = "l";
    }

    switch (cmd) {
      case "M": {
        const x = num();
        const y = num();
        if (abort) break;
        openSub(x, y);
        prevCtrlKind = "";
        break;
      }
      case "m": {
        const x = curX + num();
        const y = curY + num();
        if (abort) break;
        openSub(x, y);
        prevCtrlKind = "";
        break;
      }
      case "L": {
        const x = num();
        const y = num();
        if (!abort) lineTo(x, y);
        break;
      }
      case "l": {
        const x = curX + num();
        const y = curY + num();
        if (!abort) lineTo(x, y);
        break;
      }
      case "H": {
        const x = num();
        if (!abort) lineTo(x, curY);
        break;
      }
      case "h": {
        const x = curX + num();
        if (!abort) lineTo(x, curY);
        break;
      }
      case "V": {
        const y = num();
        if (!abort) lineTo(curX, y);
        break;
      }
      case "v": {
        const y = curY + num();
        if (!abort) lineTo(curX, y);
        break;
      }
      case "C":
      case "c": {
        const rel = cmd === "c";
        const bx = rel ? curX : 0;
        const by = rel ? curY : 0;
        const c1x = bx + num();
        const c1y = by + num();
        const c2x = bx + num();
        const c2y = by + num();
        const ex = bx + num();
        const ey = by + num();
        if (abort) break;
        cubicTo(c1x, c1y, c2x, c2y, ex, ey);
        prevCtrlX = c2x;
        prevCtrlY = c2y;
        prevCtrlKind = "C";
        break;
      }
      case "S":
      case "s": {
        const rel = cmd === "s";
        const bx = rel ? curX : 0;
        const by = rel ? curY : 0;
        let c1x: number;
        let c1y: number;
        if (prevCtrlKind === "C") {
          c1x = 2 * curX - prevCtrlX;
          c1y = 2 * curY - prevCtrlY;
        } else {
          c1x = curX;
          c1y = curY;
        }
        const c2x = bx + num();
        const c2y = by + num();
        const ex = bx + num();
        const ey = by + num();
        if (abort) break;
        cubicTo(c1x, c1y, c2x, c2y, ex, ey);
        prevCtrlX = c2x;
        prevCtrlY = c2y;
        prevCtrlKind = "C";
        break;
      }
      case "Q":
      case "q": {
        const rel = cmd === "q";
        const bx = rel ? curX : 0;
        const by = rel ? curY : 0;
        const qx = bx + num();
        const qy = by + num();
        const ex = bx + num();
        const ey = by + num();
        if (abort) break;
        const c1x = curX + (2 / 3) * (qx - curX);
        const c1y = curY + (2 / 3) * (qy - curY);
        const c2x = ex + (2 / 3) * (qx - ex);
        const c2y = ey + (2 / 3) * (qy - ey);
        cubicTo(c1x, c1y, c2x, c2y, ex, ey);
        prevCtrlX = qx;
        prevCtrlY = qy;
        prevCtrlKind = "Q";
        break;
      }
      case "T":
      case "t": {
        const rel = cmd === "t";
        const bx = rel ? curX : 0;
        const by = rel ? curY : 0;
        let qx: number;
        let qy: number;
        if (prevCtrlKind === "Q") {
          qx = 2 * curX - prevCtrlX;
          qy = 2 * curY - prevCtrlY;
        } else {
          qx = curX;
          qy = curY;
        }
        const ex = bx + num();
        const ey = by + num();
        if (abort) break;
        const c1x = curX + (2 / 3) * (qx - curX);
        const c1y = curY + (2 / 3) * (qy - curY);
        const c2x = ex + (2 / 3) * (qx - ex);
        const c2y = ey + (2 / 3) * (qy - ey);
        cubicTo(c1x, c1y, c2x, c2y, ex, ey);
        prevCtrlX = qx;
        prevCtrlY = qy;
        prevCtrlKind = "Q";
        break;
      }
      case "A":
      case "a": {
        const rel = cmd === "a";
        const rx = num();
        const ry = num();
        const xrot = num();
        const large = flag();
        const sweep = flag();
        const ex = (rel ? curX : 0) + num();
        const ey = (rel ? curY : 0) + num();
        if (abort) break;
        ensureSub();
        if (rx === 0 || ry === 0) {
          lineTo(ex, ey);
        } else {
          const ac = arcToCenter(curX, curY, ex, ey, rx, ry, xrot, large !== 0, sweep !== 0);
          if (ac) {
            flattenArcCenter(cur, mat, ac.cx, ac.cy, ac.rx, ac.ry, ac.phi, ac.theta0, ac.delta, tol, maxSteps);
            curX = ex;
            curY = ey;
          } else {
            lineTo(ex, ey);
          }
          prevCtrlKind = "";
        }
        break;
      }
      case "Z":
      case "z": {
        if (hasCur) {
          curClosed = true;
          curX = startX;
          curY = startY;
          finishSub();
        }
        prevCtrlKind = "";
        break;
      }
      default:
        abort = true;
        break;
    }
    if (abort) break;
  }
  finishSub();
  return subs;
}

/** Exposed for tests: parse an SVG path `d` string into flat polylines (mm). */
export function flattenSvgPathData(
  d: string,
  tol: number,
  maxSteps: number,
): { points: Float64Array; closed: boolean }[] {
  const t = Math.abs(tol) > 1e-9 ? Math.abs(tol) : 1e-9;
  const steps = Math.max(1, Math.min(96, Math.floor(maxSteps) || 1));
  return flattenPathCore(d, IDENTITY, t, steps);
}

// ---------------------------------------------------------------------------
// Basic-shape geometry.
// ---------------------------------------------------------------------------

function rectGeom(el: XmlElement, mat: Mat, tol: number, maxSteps: number): Float64Array | null {
  const x = fnum(el, "x", 0);
  const y = fnum(el, "y", 0);
  const w = fnum(el, "width", 0);
  const h = fnum(el, "height", 0);
  if (!(w > 0) || !(h > 0)) return null;
  let rx = optLen(el, "rx");
  let ry = optLen(el, "ry");
  if (rx == null && ry == null) {
    rx = 0;
    ry = 0;
  } else if (rx == null) {
    rx = ry as number;
  } else if (ry == null) {
    ry = rx;
  }
  rx = Math.max(0, Math.min(rx as number, w / 2));
  ry = Math.max(0, Math.min(ry as number, h / 2));

  const out: number[] = [];
  const line = (ux: number, uy: number): void => {
    const p = applyMat(mat, ux, uy);
    out.push(p.x, p.y);
  };
  if (rx <= 0 || ry <= 0) {
    line(x, y);
    line(x + w, y);
    line(x + w, y + h);
    line(x, y + h);
    return Float64Array.from(out);
  }
  const HALF = Math.PI / 2;
  line(x + rx, y);
  line(x + w - rx, y);
  flattenArcCenter(out, mat, x + w - rx, y + ry, rx, ry, 0, -HALF, HALF, tol, maxSteps);
  line(x + w, y + h - ry);
  flattenArcCenter(out, mat, x + w - rx, y + h - ry, rx, ry, 0, 0, HALF, tol, maxSteps);
  line(x + rx, y + h);
  flattenArcCenter(out, mat, x + rx, y + h - ry, rx, ry, 0, HALF, HALF, tol, maxSteps);
  line(x, y + ry);
  flattenArcCenter(out, mat, x + rx, y + ry, rx, ry, 0, Math.PI, HALF, tol, maxSteps);
  dropTrailingDup(out);
  return Float64Array.from(out);
}

function ellipseGeom(cx: number, cy: number, rx: number, ry: number, mat: Mat, tol: number, maxSteps: number): Float64Array {
  const out: number[] = [];
  const start = applyMat(mat, cx + rx, cy);
  out.push(start.x, start.y);
  flattenArcCenter(out, mat, cx, cy, rx, ry, 0, 0, 2 * Math.PI, tol, maxSteps);
  dropTrailingDup(out);
  return Float64Array.from(out);
}

// ---------------------------------------------------------------------------
// Tree walk (paint resolution shared between layer discovery and extraction).
// ---------------------------------------------------------------------------

const NON_RENDERED = new Set([
  "defs",
  "clippath",
  "mask",
  "symbol",
  "metadata",
  "title",
  "desc",
  "style",
  "pattern",
  "marker",
  "lineargradient",
  "radialgradient",
  "filter",
  "script",
  "foreignobject",
]);

const CONTAINERS = new Set(["g", "svg", "a", "switch"]);
const DRAWABLES = new Set(["path", "rect", "circle", "ellipse", "line", "polyline", "polygon"]);

type WarnFn = (kind: "skip" | "warn", message: string) => void;
type DrawFn = (el: XmlElement, kind: string, paint: string, mat: Mat) => void;

/** Guard against pathologically deep <g> nesting overflowing the stack. */
const MAX_NEST_DEPTH = 512;

function walkTree(el: XmlElement, mat: Mat, ctx: Ctx, warn: WarnFn, onDraw: DrawFn, depth = 0): void {
  const tag = el.localName.toLowerCase();
  if (NON_RENDERED.has(tag)) return;

  const style = parseStyle(el);
  const display = styleProp(el, style, "display");
  if (display != null && display.trim().toLowerCase() === "none") return;

  const childCtx = updateCtx(el, ctx, style);
  const tmat = matMul(mat, parseTransform(el.attr("transform")));

  if (CONTAINERS.has(tag)) {
    if (depth >= MAX_NEST_DEPTH) {
      warn("warn", "Some groups were nested too deeply to import — flatten or ungroup them in your editor, then re-open the file");
      return;
    }
    for (const c of childElements(el)) walkTree(c, tmat, childCtx, warn, onDraw, depth + 1);
    return;
  }
  if (tag === "use") {
    warn("warn", "Reused <use> elements are not imported — expand them in your editor first");
    return;
  }
  if (tag === "text" || tag === "tspan" || tag === "textpath") {
    warn("warn", "Text is not imported — convert text to paths first");
    return;
  }
  if (tag === "image") {
    warn("warn", "Embedded images are not imported — raster content is skipped");
    return;
  }
  if (DRAWABLES.has(tag)) {
    if (childCtx.visibility === "hidden") return;
    onDraw(el, tag, paintOf(childCtx), tmat);
    return;
  }
  // Unknown element: ignore (do not descend — avoids surprises from metadata-like nodes).
}

// ---------------------------------------------------------------------------
// Layer mapping.
// ---------------------------------------------------------------------------

const PALETTE_INDEX = new Map<string, number>();
LIGHTBURN_PALETTE.forEach((c, i) => PALETTE_INDEX.set(c.toUpperCase(), i));

function assignLayers(encounter: string[]): Map<string, number> {
  const layerOf = new Map<string, number>();
  const used = new Set<number>();
  for (const hex of encounter) {
    const pi = PALETTE_INDEX.get(hex);
    if (pi !== undefined && !layerOf.has(hex)) {
      layerOf.set(hex, pi);
      used.add(pi);
    }
  }
  let next = 0;
  for (const hex of encounter) {
    if (layerOf.has(hex)) continue;
    while (used.has(next)) next++;
    layerOf.set(hex, next);
    used.add(next);
    next++;
  }
  return layerOf;
}

function buildLayers(layerOf: Map<string, number>, counts: Map<string, number>): LayerInfo[] {
  const layers: LayerInfo[] = [];
  for (const [hex, index] of layerOf) {
    layers.push({ index, name: hex, color: hex, shapeCount: counts.get(hex) ?? 0 });
  }
  layers.sort((a, b) => a.index - b.index);
  return layers;
}

// ---------------------------------------------------------------------------
// Geometry extraction.
// ---------------------------------------------------------------------------

function addWarn(map: Map<string, ImportWarning>, kind: "skip" | "warn", message: string): void {
  const ex = map.get(message);
  if (ex) ex.count++;
  else map.set(message, { kind, message, count: 1 });
}

function emitGeom(
  el: XmlElement,
  kind: string,
  layer: number,
  mat: Mat,
  tol: number,
  steps: number,
  geoms: PolyGeom[],
  warn: WarnFn,
): void {
  switch (kind) {
    case "path": {
      const d = el.attr("d");
      if (!d) {
        warn("skip", "Path with empty geometry");
        return;
      }
      for (const s of flattenPathCore(d, mat, tol, steps)) {
        if (s.points.length >= 4) geoms.push({ layer, points: s.points, closed: s.closed, source: "path" });
      }
      return;
    }
    case "rect": {
      const r = rectGeom(el, mat, tol, steps);
      if (r) geoms.push({ layer, points: r, closed: true, source: "rect" });
      else warn("skip", "Rectangle with non-positive size skipped");
      return;
    }
    case "circle": {
      const cx = fnum(el, "cx", 0);
      const cy = fnum(el, "cy", 0);
      const r = fnum(el, "r", 0);
      if (r > 0) geoms.push({ layer, points: ellipseGeom(cx, cy, r, r, mat, tol, steps), closed: true, source: "circle" });
      else warn("skip", "Circle with non-positive radius skipped");
      return;
    }
    case "ellipse": {
      const cx = fnum(el, "cx", 0);
      const cy = fnum(el, "cy", 0);
      const rx = fnum(el, "rx", 0);
      const ry = fnum(el, "ry", 0);
      if (rx > 0 && ry > 0) {
        geoms.push({ layer, points: ellipseGeom(cx, cy, rx, ry, mat, tol, steps), closed: true, source: "ellipse" });
      } else {
        warn("skip", "Ellipse with non-positive radius skipped");
      }
      return;
    }
    case "line": {
      const pts = [fnum(el, "x1", 0), fnum(el, "y1", 0), fnum(el, "x2", 0), fnum(el, "y2", 0)];
      geoms.push({ layer, points: transformFlat(pts, mat), closed: false, source: "line" });
      return;
    }
    case "polyline": {
      const pts = parsePoints(el.attr("points"));
      if (pts.length >= 4) geoms.push({ layer, points: transformFlat(pts, mat), closed: false, source: "polyline" });
      else warn("skip", "Polyline with fewer than two points skipped");
      return;
    }
    case "polygon": {
      const pts = parsePoints(el.attr("points"));
      if (pts.length >= 6) {
        const flat = transformFlat(pts, mat);
        const arr = Array.from(flat);
        dropTrailingDup(arr);
        geoms.push({ layer, points: Float64Array.from(arr), closed: true, source: "polygon" });
      } else {
        warn("skip", "Polygon with fewer than three points skipped");
      }
      return;
    }
    default:
      return;
  }
}

function extractSvg(
  root: XmlElement,
  rootMat: Mat,
  layerOf: Map<string, number>,
  selected: ReadonlySet<number>,
  curveSteps: number,
): { geoms: PolyGeom[]; warnings: ImportWarning[] } {
  const steps = Math.max(3, Math.min(96, Math.trunc(curveSteps) || 16));
  const tol = 0.4 / steps;
  const geoms: PolyGeom[] = [];
  const warnMap = new Map<string, ImportWarning>();
  const warn: WarnFn = (kind, message) => addWarn(warnMap, kind, message);
  walkTree(root, rootMat, INITIAL_CTX, warn, (el, kind, paint, mat) => {
    const layer = layerOf.get(paint) ?? 0;
    if (!selected.has(layer)) return;
    emitGeom(el, kind, layer, mat, tol, steps, geoms, warn);
  });
  return { geoms, warnings: [...warnMap.values()] };
}

// ---------------------------------------------------------------------------
// Root transform (units -> mm).
// ---------------------------------------------------------------------------

interface ViewBox {
  minX: number;
  minY: number;
  w: number;
  h: number;
}

function parseViewBox(raw: string | null): ViewBox | null {
  if (!raw) return null;
  const p = raw
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (p.length < 4 || p.some((v) => !Number.isFinite(v))) return null;
  return { minX: p[0], minY: p[1], w: p[2], h: p[3] };
}

function computeRootMatrix(root: XmlElement): Mat {
  const vb = parseViewBox(root.attr("viewBox", "viewbox"));
  const wLen = parseSvgLength(root.attr("width"));
  const hLen = parseSvgLength(root.attr("height"));
  let scaleX = 1;
  let scaleY = 1;
  const wUnit = wLen ? unitToMm(wLen.unit) : null;
  const hUnit = hLen ? unitToMm(hLen.unit) : null;
  if (vb && vb.w > 0 && wLen && wUnit != null) scaleX = (wLen.value * wUnit) / vb.w;
  if (vb && vb.h > 0 && hLen && hUnit != null) scaleY = (hLen.value * hUnit) / vb.h;
  const wPhys = wUnit != null && vb != null && vb.w > 0;
  const hPhys = hUnit != null && vb != null && vb.h > 0;
  if (wPhys && !hPhys) scaleY = scaleX;
  if (hPhys && !wPhys) scaleX = scaleY;
  const minX = vb ? vb.minX : 0;
  const minY = vb ? vb.minY : 0;
  return [scaleX, 0, 0, scaleY, -scaleX * minX, -scaleY * minY];
}

// ---------------------------------------------------------------------------
// Public entry point.
// ---------------------------------------------------------------------------

function emptyDoc(fileName: string, svgText: string, message: string): VectorDocument {
  return {
    kind: "svg",
    fileName,
    layers: [],
    sourceText: svgText,
    extractGeometry: () => ({ geoms: [], warnings: [{ kind: "warn", message, count: 1 }] }),
  };
}

/** Largest SVG text we will parse before assuming the file is not a real drawing. */
const MAX_SVG_CHARS = 256 * 1024 * 1024; // ~256 MB of text

export function parseSvgVector(svgText: string, fileName: string): VectorDocument {
  if (svgText.length > MAX_SVG_CHARS) {
    throw new Error(
      "This SVG is larger than 256 MB, which is far bigger than any real drawing. " +
        "It may be the wrong file — export a smaller SVG or pick a different file.",
    );
  }
  const parsed = parseXml(svgText);
  const root = parsed ? findSvg(parsed) : null;
  if (!root) {
    return emptyDoc(fileName, svgText, "File is not a valid SVG document");
  }
  const rootMat = computeRootMatrix(root);

  const encounter: string[] = [];
  const seen = new Set<string>();
  const counts = new Map<string, number>();
  const noWarn: WarnFn = () => {};
  walkTree(root, rootMat, INITIAL_CTX, noWarn, (_el, _kind, paint) => {
    if (!seen.has(paint)) {
      seen.add(paint);
      encounter.push(paint);
    }
    counts.set(paint, (counts.get(paint) ?? 0) + 1);
  });

  const layerOf = assignLayers(encounter);
  const layers = buildLayers(layerOf, counts);

  return {
    kind: "svg",
    fileName,
    layers,
    sourceText: svgText,
    extractGeometry: (selectedLayers, curveSteps) => extractSvg(root, rootMat, layerOf, selectedLayers, curveSteps),
  };
}
